import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

/**
 * Exposes an interface similar to useState while using the search params in the
 * location to store the state.
 *
 * @param key Name of the search param.
 */
const useSearchParam = <T = string>(
  key: string
): [T, (value: string) => void] => {
  const location = useLocation()
  const history = useHistory()

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const value = searchParams.get(key) as T

  const setValue = useCallback(
    (newValue: string) => {
      searchParams.set(key, newValue)
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString()
      })
    },
    [key, history, location.pathname, searchParams]
  )

  return [value, setValue]
}

export default useSearchParam
